/**
 * Utilities for building Excel export data from rate schedules
 *
 * These functions transform rate schedule data into exportable row/column structures
 * for Excel, without relying on AgGrid's rendering.
 */

import { logger } from "@platform/browser-logger";
import {
  coverageTierToLabel,
  coverageTierToLabelLong,
  createAgeBandKey,
  extractUniqueAgeBands,
  extractUniqueTiers,
  formatAgeBand,
  formatCellValue,
  formatRateValue,
  getQuantityLabel,
  getUnitLabel,
  groupRatesByAgeBandAndTier,
  groupRatesByAgeBandGender,
  groupRatesByAgeBandTobacco,
  hasEmployeeSpouseSplit,
  isCompanionSchedule,
  resolveAdndRateFromPrimarySchedule,
  type BenefitPlan,
  type BenefitPlanGroup,
  type Rate,
  type RateSchedule,
} from "@platform/btx-rates";
import {
  CoverageTierEnum,
  RateDesignEnum,
  RateUnitEnum,
} from "@platform/openapi-types";

/**
 * Represents a row in the Excel export
 * Each row has a label and values for each plan
 */
export interface ExcelRow {
  /** Row label (e.g., "Employee Only", "Rate", etc.) */
  label: string;
  /** Map of plan ID to cell value */
  values: Record<string, string | number>;
  /**
   * When true, this row is a plan-group-level total whose value is placed on
   * the group's first plan and merged across the group's columns.
   */
  isGroupTotal?: boolean;
}

interface RateRowSection {
  key: string;
  rows: ExcelRow[];
}

const FLAT_RATE_DEPENDENT_TIERS = [
  CoverageTierEnum.employeeDependent,
  CoverageTierEnum.spouseOnly,
  CoverageTierEnum.childOnly,
] as const;

type FlatRateDependentTier = (typeof FLAT_RATE_DEPENDENT_TIERS)[number];

const isFlatRateDependentTier = (
  coverageTierCode: CoverageTierEnum | undefined,
): coverageTierCode is FlatRateDependentTier => {
  if (coverageTierCode == null) {
    return false;
  }

  return FLAT_RATE_DEPENDENT_TIERS.includes(
    coverageTierCode as FlatRateDependentTier,
  );
};

function createHeaderRow(label: string, planId: string): ExcelRow {
  return { label, values: { [planId]: "" } };
}

function createColumnHeaderRow(
  label: string,
  planId: string,
  columns: string,
): ExcelRow {
  return { label, values: { [planId]: columns } };
}

const buildSectionRowKey = (
  sectionKey: string,
  row: ExcelRow,
  planId: string,
): string => `${sectionKey}:${row.label || String(row.values[planId] ?? "")}`;

const mergeSectionRow = (
  plan: BenefitPlan,
  sectionKey: string,
  row: ExcelRow,
  orderedRowKeys: string[],
  rowsByKey: Map<string, ExcelRow>,
): void => {
  const rowKey = buildSectionRowKey(sectionKey, row, plan.id);
  const existingRow = rowsByKey.get(rowKey);

  if (!existingRow) {
    orderedRowKeys.push(rowKey);
    rowsByKey.set(rowKey, row);
    return;
  }

  rowsByKey.set(rowKey, {
    label: existingRow.label || row.label,
    values: { ...existingRow.values, ...row.values },
  });
};

const mergeSectionRows = (
  plan: BenefitPlan,
  section: RateRowSection,
  orderedRowKeys: string[],
  rowsByKey: Map<string, ExcelRow>,
): void => {
  section.rows.forEach((row) => {
    mergeSectionRow(plan, section.key, row, orderedRowKeys, rowsByKey);
  });
};

const buildPrimaryRateSection = (
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): RateRowSection => {
  const rateDesign = rateSchedule.rateDesign;

  switch (rateDesign) {
    case RateDesignEnum.composite:
      return {
        key: `primary:${rateDesign}`,
        rows: buildCompositeRateRows(rateSchedule, plan),
      };
    case RateDesignEnum.ageBanded:
      return {
        key: `primary:${rateDesign}`,
        rows: buildAgeBandedRateRows(rateSchedule, plan),
      };
    case RateDesignEnum.ageBandedTobacco:
      return {
        key: `primary:${rateDesign}`,
        rows: buildAgeBandedTobaccoRateRows(rateSchedule, plan),
      };
    case RateDesignEnum.ageBandedGender:
      return {
        key: `primary:${rateDesign}`,
        rows: buildAgeBandedGenderRateRows(rateSchedule, plan),
      };
    case RateDesignEnum.ageBandedTiered:
      return {
        key: `primary:${rateDesign}`,
        rows: buildAgeBandedTieredRateRows(rateSchedule, plan),
      };
    case RateDesignEnum.flatRate:
      return {
        key: `primary:${rateDesign}`,
        rows: buildFlatRateRows(rateSchedule, plan),
      };
    case RateDesignEnum.gender:
      return {
        key: `primary:${rateDesign}`,
        rows: buildGenderRateRows(rateSchedule, plan),
      };
    default:
      logger.warn("[btx export] Unsupported rate design", {
        rateDesign,
        planId: plan.id,
      });
      return {
        key: `primary:${rateDesign}`,
        rows: [
          {
            label: `${rateDesign} (not yet implemented)`,
            values: { [plan.id]: "" },
          },
        ],
      };
  }
};

const buildCompanionRateRows = (
  rateSchedules: RateSchedule[],
  plan: BenefitPlan,
  includeRateBasisInLabel: boolean,
): ExcelRow[] => {
  const rowsByLabel = new Map<string, { rate: Rate; isPepm: boolean }>();

  const getCompanionRateUpdatedAt = (rate: Rate): number => {
    const updatedAt = Date.parse(rate.updatedAt ?? "");

    return Number.isNaN(updatedAt) ? 0 : updatedAt;
  };

  rateSchedules.forEach((rateSchedule) => {
    const isPepm = rateSchedule.pepm != null;

    (rateSchedule.rates || []).forEach((rate) => {
      const label = getDependentRowLabel(
        rate.coverageTierCode,
        rate.unit,
        includeRateBasisInLabel,
      );

      const existingRate = rowsByLabel.get(label)?.rate;
      if (
        existingRate == null ||
        getCompanionRateUpdatedAt(rate) >
          getCompanionRateUpdatedAt(existingRate)
      ) {
        rowsByLabel.set(label, { rate, isPepm });
      }
    });
  });

  return Array.from(rowsByLabel.entries()).map(([label, entry]) => {
    const { rate, isPepm } = entry;
    const quantityValue =
      formatQuantityValue(rate, isPepm) ||
      (rate.numberOfLives != null ? String(rate.numberOfLives) : "");
    const rateValue = `$${formatRateValue(rate.rate, rate.unit)}`;

    return {
      label,
      values: {
        [plan.id]:
          quantityValue === "" ? rateValue : `${quantityValue} | ${rateValue}`,
      },
    };
  });
};

const isFlatRateDependentRate = (rate: Rate): boolean =>
  isFlatRateDependentTier(rate.coverageTierCode);

const getDependentTierLabel = (
  coverageTierCode: CoverageTierEnum | undefined,
): string => {
  switch (coverageTierCode) {
    case CoverageTierEnum.spouseOnly:
      return "Spouse";
    case CoverageTierEnum.childOnly:
      return "Child";
    case CoverageTierEnum.employeeDependent:
    default:
      return "Dependent";
  }
};

/**
 * Dependent row label. When `includeRateBasisInLabel` is true (behind
 * BTX_DEPENDENT_RATE_BASIS), includes the rate basis (e.g. "Dependent Rate
 * per Unit", "Spouse Rate per $1,000") so a flat USD dependent rate can't be
 * mistaken for the tier label alone — getUnitLabel's generic "Rate" for usd
 * doesn't distinguish unit-based dependent rates from EE's own. Otherwise
 * falls back to the plain tier label, matching legacy export output.
 */
const getDependentRowLabel = (
  coverageTierCode: CoverageTierEnum | undefined,
  unit: string | undefined,
  includeRateBasisInLabel: boolean,
): string => {
  const tierLabel = getDependentTierLabel(coverageTierCode);
  if (!includeRateBasisInLabel) {
    return tierLabel;
  }
  const rateLabel = unit === "usd" ? "Rate per Unit" : getUnitLabel(unit);
  return `${tierLabel} ${rateLabel}`;
};

const buildFlatRateDependentRows = (
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
  includeRateBasisInLabel: boolean,
): ExcelRow[] => {
  const dependentRates = (rateSchedule.rates || []).filter(
    isFlatRateDependentRate,
  );
  if (dependentRates.length === 0) {
    return [];
  }

  const isPepm = rateSchedule.pepm != null;

  return dependentRates.map((rate) => {
    const quantityValue =
      formatQuantityValue(rate, isPepm) ||
      (rate.numberOfLives != null ? String(rate.numberOfLives) : "");
    const rateValue = `$${formatRateValue(rate.rate, rate.unit)}`;

    return {
      label: getDependentRowLabel(
        rate.coverageTierCode,
        rate.unit,
        includeRateBasisInLabel,
      ),
      values: {
        [plan.id]:
          quantityValue === "" ? rateValue : `${quantityValue} | ${rateValue}`,
      },
    };
  });
};

const filterRowsMissingLabels = (
  rows: ExcelRow[],
  existingRows: ExcelRow[],
): ExcelRow[] => {
  const existingLabels = new Set(existingRows.map((row) => row.label));

  return rows.filter((row) => !existingLabels.has(row.label));
};

const buildRateSectionsForPlan = (
  plan: BenefitPlan,
  rateSchedules: RateSchedule[],
  includeDependentRateBasisInLabel: boolean,
): RateRowSection[] => {
  if (!rateSchedules || rateSchedules.length === 0) {
    return [];
  }

  const rateSchedule = rateSchedules.find(
    (schedule) =>
      schedule.rateCoverageIndicator !== "adnd" &&
      !isCompanionSchedule(schedule, rateSchedules),
  );
  const companionRateSchedules = rateSchedules.filter((schedule) =>
    isCompanionSchedule(schedule, rateSchedules),
  );
  const adndSchedule = rateSchedules.find(
    (schedule) => schedule.rateCoverageIndicator === "adnd",
  );

  const sections: RateRowSection[] = [];
  let flatRateDependentRows: ExcelRow[] = [];

  if (rateSchedule) {
    sections.push(buildPrimaryRateSection(rateSchedule, plan));

    if (rateSchedule.rateDesign === RateDesignEnum.flatRate) {
      flatRateDependentRows = buildFlatRateDependentRows(
        rateSchedule,
        plan,
        includeDependentRateBasisInLabel,
      );
      if (flatRateDependentRows.length > 0) {
        sections.push({
          key: "secondary:flat-dependents",
          rows: flatRateDependentRows,
        });
      }
    }
  }

  if (companionRateSchedules.length > 0) {
    let companionRows = buildCompanionRateRows(
      companionRateSchedules,
      plan,
      includeDependentRateBasisInLabel,
    );

    if (rateSchedule?.rateDesign === RateDesignEnum.flatRate) {
      companionRows = filterRowsMissingLabels(
        companionRows,
        flatRateDependentRows,
      );
    }

    if (companionRows.length > 0) {
      sections.push({
        key: "secondary:companion",
        rows: companionRows,
      });
    }
  }

  if (adndSchedule) {
    sections.push({
      key: "secondary:adnd",
      rows: buildAdndRateRows(adndSchedule, plan),
    });
  }

  return sections;
};

function findRateForAgeBandAndTier(
  rates: Rate[] | undefined,
  ageBandKey: string,
  tierCode: CoverageTierEnum,
): Rate | undefined {
  if (!rates) return undefined;

  return rates.find((rate) => {
    const rateKey = createAgeBandKey(rate.ageBandLower, rate.ageBandUpper);
    return rateKey === ageBandKey && rate.coverageTierCode === tierCode;
  });
}

function findRateForAgeBand(
  rates: Rate[] | undefined,
  ageBandKey: string,
): Rate | undefined {
  if (!rates) return undefined;

  return rates.find((rate) => {
    const rateKey = createAgeBandKey(rate.ageBandLower, rate.ageBandUpper);
    return rateKey === ageBandKey;
  });
}

/**
 * Build Excel rows for composite/tiered rate design
 *
 * Structure:
 * - Header row: "Tiered Rates"
 * - One row per tier with: Tier Label, Number of Lives, Rate
 */
export function buildCompositeRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): ExcelRow[] {
  const rates = rateSchedule.rates || [];
  const tierCount = extractUniqueTiers(rates).length;

  const dataRows = rates.map((rate) => ({
    label: coverageTierToLabelLong(rate.coverageTierCode, tierCount),
    values: {
      [plan.id]: `${rate.numberOfLives ?? ""} | $${formatRateValue(rate.rate, rate.unit)}`,
    },
  }));

  return [
    createHeaderRow("Tiered Rates", plan.id),
    createColumnHeaderRow("Coverage Tier", plan.id, "Enrolled Lives | Rate"),
    ...dataRows,
  ];
}

/**
 * Build Excel rows for flat rate design
 *
 * Structure:
 * - Header row: "Flat" or "PEPM"
 * - Single column layout: Quantity Label, Rate
 * - Dual column layout (gender/tobacco): Label, Primary, Secondary
 */
function formatQuantityValue(
  rate: Rate | undefined,
  isPepm: boolean,
): string | number {
  if (!rate) return "";
  if (isPepm) return rate.numberOfLives ?? "";

  if (rate.unit === RateUnitEnum.usd) {
    return formatCellValue(rate.numberOfLives);
  }

  const volume = rate.volume;
  if (volume == null) return "";

  return `$${new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(volume)}`;
}

function buildFlatRateGenderRows(
  plan: BenefitPlan,
  rates: Rate[],
  isPepm: boolean,
): ExcelRow[] {
  const findGenderRate = (gender: "male" | "female"): Rate | undefined => {
    let fallback: Rate | undefined;
    for (const r of rates) {
      if (r.gender !== gender) continue;
      if (!isPepm) return r;
      if (r.unit === RateUnitEnum.totalPepm) return r;
      fallback ??= r;
    }
    return fallback;
  };

  const maleRate = findGenderRate("male");
  const femaleRate = findGenderRate("female");
  const quantityLabel = getQuantityLabel(maleRate?.unit, isPepm);
  const unitLabel = isPepm ? "Rate" : getUnitLabel(maleRate?.unit);

  return [
    createColumnHeaderRow("", plan.id, "Male | Female"),
    {
      label: quantityLabel,
      values: {
        [plan.id]: `${formatQuantityValue(maleRate, isPepm)} | ${formatQuantityValue(femaleRate, isPepm)}`,
      },
    },
    {
      label: unitLabel,
      values: {
        [plan.id]: `$${formatRateValue(maleRate?.rate, maleRate?.unit)} | $${formatRateValue(femaleRate?.rate, femaleRate?.unit)}`,
      },
    },
  ];
}

function buildFlatRateTobaccoRows(
  plan: BenefitPlan,
  rates: Rate[],
  isPepm: boolean,
): ExcelRow[] {
  const tobaccoRate = rates.find((r) => r.isTobaccoRated === true);
  const nonTobaccoRate = rates.find((r) => r.isTobaccoRated === false);
  const quantityLabel = getQuantityLabel(tobaccoRate?.unit, isPepm);
  const unitLabel = isPepm ? "Rate" : getUnitLabel(tobaccoRate?.unit);

  return [
    createColumnHeaderRow("", plan.id, "Tobacco | Non-Tobacco"),
    {
      label: quantityLabel,
      values: {
        [plan.id]: `${formatQuantityValue(tobaccoRate, isPepm)} | ${formatQuantityValue(nonTobaccoRate, isPepm)}`,
      },
    },
    {
      label: unitLabel,
      values: {
        [plan.id]: `$${formatRateValue(tobaccoRate?.rate, tobaccoRate?.unit)} | $${formatRateValue(nonTobaccoRate?.rate, nonTobaccoRate?.unit)}`,
      },
    },
  ];
}

function buildFlatRateSimpleRows(
  plan: BenefitPlan,
  rate: Rate,
  isPepm: boolean,
): ExcelRow[] {
  const quantityLabel = getQuantityLabel(rate?.unit, isPepm);
  const unitLabel = isPepm ? "Rate" : getUnitLabel(rate?.unit);

  return [
    {
      label: quantityLabel,
      values: { [plan.id]: formatQuantityValue(rate, isPepm) },
    },
    {
      label: unitLabel,
      values: { [plan.id]: `$${formatRateValue(rate?.rate, rate?.unit)}` },
    },
  ];
}

export function buildFlatRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): ExcelRow[] {
  const rates = rateSchedule.rates || [];
  if (rates.length === 0) return [];

  const isPepm =
    rateSchedule.pepm != null ||
    rates.some(
      (r) =>
        r.unit === RateUnitEnum.totalPepm ||
        r.unit === RateUnitEnum.fmlaPepm ||
        r.unit === RateUnitEnum.adaPepm,
    );
  const rateTypeLabel = isPepm ? "PEPM" : "Flat";
  const hasGender = rates.some((r) => r.gender != null);
  const hasTobacco = rates.some((r) => r.isTobaccoRated != null);

  const rows = [createHeaderRow(rateTypeLabel, plan.id)];

  if (hasGender) {
    rows.push(...buildFlatRateGenderRows(plan, rates, isPepm));
  } else if (hasTobacco) {
    rows.push(...buildFlatRateTobaccoRows(plan, rates, isPepm));
  } else {
    const primaryRate = isPepm
      ? (rates.find((r) => r.unit === RateUnitEnum.totalPepm) ?? rates[0])
      : rates[0];
    rows.push(...buildFlatRateSimpleRows(plan, primaryRate, isPepm));
  }

  return rows;
}

/**
 * Build Excel rows for gender rate design
 *
 * Structure:
 * - Header row: "Gender Rates"
 * - Column headers: Male, Female
 * - Rate row: Male rate, Female rate
 */
export function buildGenderRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): ExcelRow[] {
  const rates = rateSchedule.rates || [];
  const maleRate = rates.find((r) => r.gender === "male");
  const femaleRate = rates.find((r) => r.gender === "female");

  return [
    createHeaderRow("Gender Rates", plan.id),
    createColumnHeaderRow("", plan.id, "Male | Female"),
    {
      label: "Rate",
      values: {
        [plan.id]: `$${formatRateValue(maleRate?.rate, maleRate?.unit)} | $${formatRateValue(femaleRate?.rate, femaleRate?.unit)}`,
      },
    },
  ];
}

/** Coverage tier codes for AD&D dependent rates */
const ADND_DEPENDENT_TIERS = [
  CoverageTierEnum.employeeDependent,
  CoverageTierEnum.spouseOnly,
  CoverageTierEnum.childOnly,
] as const;

const isAdndDependentRate = (rate: Rate): boolean =>
  ADND_DEPENDENT_TIERS.includes(
    rate.coverageTierCode as (typeof ADND_DEPENDENT_TIERS)[number],
  );

const getAdndRateLabel = (rate: Rate): string => {
  switch (rate.coverageTierCode) {
    case CoverageTierEnum.spouseOnly:
      return "Spouse AD&D";
    case CoverageTierEnum.childOnly:
      return "Child AD&D";
    case CoverageTierEnum.employeeDependent:
      return "Dependent AD&D";
    default:
      return "Employee AD&D";
  }
};

/**
 * Build Excel rows for AD&D rate schedule
 *
 * AD&D rates are always flat (never age-banded, gendered, tobacco, or tiered).
 * Renders employee rates first, then dependent rates.
 */
export function buildAdndRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
  primarySchedule?: RateSchedule,
): ExcelRow[] {
  const rates = rateSchedule.rates || [];
  if (rates.length === 0) {
    return [];
  }

  const [employeeRates, dependentRates] = rates.reduce<[Rate[], Rate[]]>(
    (acc, rate) => {
      if (isAdndDependentRate(rate)) {
        acc[1].push(rate);
      } else {
        acc[0].push(rate);
      }
      return acc;
    },
    [[], []],
  );

  const rows: ExcelRow[] = [...employeeRates, ...dependentRates].map((rate) => {
    const label = getAdndRateLabel(rate);
    const resolvedRate = resolveAdndRateFromPrimarySchedule(
      rate,
      primarySchedule,
    );

    return {
      label,
      values: { [plan.id]: `$${formatRateValue(resolvedRate, rate.unit)}` },
    };
  });
  // Prepend AD&D header row
  rows.unshift(createHeaderRow("AD&D Rates", plan.id));

  return rows;
}

export function buildAgeBandedTieredRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): ExcelRow[] {
  const rates = rateSchedule.rates || [];
  const uniqueTiers = extractUniqueTiers(rates);
  const ageBandGroups = groupRatesByAgeBandAndTier(rates);
  const tierLabels = uniqueTiers.map(coverageTierToLabel).join(" | ");
  const subHeaderParts = uniqueTiers.flatMap(() => ["Enrolled", "Rate"]);

  const dataRows = ageBandGroups.map((group) => ({
    label: formatAgeBand(group.lower, group.upper),
    values: {
      [plan.id]: uniqueTiers
        .flatMap((tier) => {
          const rate = group.ratesByTier.get(tier);
          return [
            formatCellValue(rate?.numberOfLives),
            `$${formatRateValue(rate?.rate, rate?.unit)}`,
          ];
        })
        .join(" | "),
    },
  }));

  return [
    createHeaderRow("Age Banded Tiered Rates", plan.id),
    createColumnHeaderRow("Age Band", plan.id, tierLabels),
    createColumnHeaderRow("", plan.id, subHeaderParts.join(" | ")),
    ...dataRows,
  ];
}

const formatEmpSpouseRateEntry = (rate: Rate | undefined): string[] => [
  formatCellValue(rate?.volume ?? rate?.numberOfLives),
  `$${formatRateValue(rate?.rate, rate?.unit)}`,
];

export function buildAgeBandedTobaccoRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): ExcelRow[] {
  const ageBandGroups = groupRatesByAgeBandTobacco(rateSchedule.rates || []);
  const hasEmpSpouseSplit = ageBandGroups.some(
    (g) =>
      g.tobaccoEeRate ??
      g.tobaccoSpRate ??
      g.nonTobaccoEeRate ??
      g.nonTobaccoSpRate,
  );

  if (hasEmpSpouseSplit) {
    const dataRows = ageBandGroups.map((group) => ({
      label: formatAgeBand(group.lower, group.upper),
      values: {
        [plan.id]: [
          ...formatEmpSpouseRateEntry(group.nonTobaccoEeRate),
          ...formatEmpSpouseRateEntry(group.nonTobaccoSpRate),
          ...formatEmpSpouseRateEntry(group.tobaccoEeRate),
          ...formatEmpSpouseRateEntry(group.tobaccoSpRate),
        ].join(" | "),
      },
    }));
    return [
      createHeaderRow("Age Banded Tobacco Rates", plan.id),
      createColumnHeaderRow(
        "Age Band",
        plan.id,
        "Non-Tobacco EE Enrolled | Non-Tobacco EE Rate | Non-Tobacco SP Enrolled | Non-Tobacco SP Rate | Tobacco EE Enrolled | Tobacco EE Rate | Tobacco SP Enrolled | Tobacco SP Rate",
      ),
      ...dataRows,
    ];
  }

  const dataRows = ageBandGroups.map((group) => ({
    label: formatAgeBand(group.lower, group.upper),
    values: {
      [plan.id]: `${formatCellValue(group.tobaccoRate?.numberOfLives)} | $${formatRateValue(group.tobaccoRate?.rate, group.tobaccoRate?.unit)} | ${formatCellValue(group.nonTobaccoRate?.numberOfLives)} | $${formatRateValue(group.nonTobaccoRate?.rate, group.nonTobaccoRate?.unit)}`,
    },
  }));

  return [
    createHeaderRow("Age Banded Tobacco Rates", plan.id),
    createColumnHeaderRow(
      "Age Band",
      plan.id,
      "Tobacco Enrolled | Tobacco Rate | Non-Tobacco Enrolled | Non-Tobacco Rate",
    ),
    ...dataRows,
  ];
}

export function buildAgeBandedGenderRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): ExcelRow[] {
  const ageBandGroups = groupRatesByAgeBandGender(rateSchedule.rates || []);
  const hasEmpSpouseSplit = ageBandGroups.some(
    (g) => g.maleEeRate ?? g.maleSpRate ?? g.femaleEeRate ?? g.femaleSpRate,
  );

  if (hasEmpSpouseSplit) {
    const dataRows = ageBandGroups.map((group) => ({
      label: formatAgeBand(group.lower, group.upper),
      values: {
        [plan.id]: [
          ...formatEmpSpouseRateEntry(group.maleEeRate),
          ...formatEmpSpouseRateEntry(group.maleSpRate),
          ...formatEmpSpouseRateEntry(group.femaleEeRate),
          ...formatEmpSpouseRateEntry(group.femaleSpRate),
        ].join(" | "),
      },
    }));
    return [
      createHeaderRow("Age Banded Gender Rates", plan.id),
      createColumnHeaderRow(
        "Age Band",
        plan.id,
        "Male EE Enrolled | Male EE Rate | Male SP Enrolled | Male SP Rate | Female EE Enrolled | Female EE Rate | Female SP Enrolled | Female SP Rate",
      ),
      ...dataRows,
    ];
  }

  const dataRows = ageBandGroups.map((group) => ({
    label: formatAgeBand(group.lower, group.upper),
    values: {
      [plan.id]: `${formatCellValue(group.maleRate?.numberOfLives)} | $${formatRateValue(group.maleRate?.rate, group.maleRate?.unit)} | ${formatCellValue(group.femaleRate?.numberOfLives)} | $${formatRateValue(group.femaleRate?.rate, group.femaleRate?.unit)}`,
    },
  }));

  return [
    createHeaderRow("Age Banded Gender Rates", plan.id),
    createColumnHeaderRow(
      "Age Band",
      plan.id,
      "Male Enrolled | Male Rate | Female Enrolled | Female Rate",
    ),
    ...dataRows,
  ];
}

/**
 * Build Excel rows for age banded rate design
 *
 * Structure:
 * - Header row: "Age Banded Rates"
 * - Simple layout: Age Band, Vol, Rate
 * - EE/SP split layout: Age Band, EE Vol, SP Vol, EE Rate, SP Rate
 */
export function buildAgeBandedRateRows(
  rateSchedule: RateSchedule,
  plan: BenefitPlan,
): ExcelRow[] {
  const rates = rateSchedule.rates || [];
  const hasEESplit = hasEmployeeSpouseSplit(rates);
  const ageBands = extractUniqueAgeBands(rates);

  if (hasEESplit) {
    const dataRows = ageBands.map((band) => {
      const eeRate = findRateForAgeBandAndTier(
        rates,
        band.key,
        CoverageTierEnum.employee,
      );
      const spRate = findRateForAgeBandAndTier(
        rates,
        band.key,
        CoverageTierEnum.spouseOnly,
      );

      return {
        label: formatAgeBand(band.lower, band.upper),
        values: {
          [plan.id]: `${formatQuantityValue(eeRate, false)} | ${formatQuantityValue(spRate, false)} | $${formatRateValue(eeRate?.rate, eeRate?.unit)} | $${formatRateValue(spRate?.rate, spRate?.unit)}`,
        },
      };
    });

    return [
      createHeaderRow("Age Banded Rates", plan.id),
      createColumnHeaderRow(
        "Age Band",
        plan.id,
        "EE Vol | SP Vol | EE Rate | SP Rate",
      ),
      ...dataRows,
    ];
  }

  const dataRows = ageBands.map((band) => {
    const rate = findRateForAgeBand(rates, band.key);

    return {
      label: formatAgeBand(band.lower, band.upper),
      values: {
        [plan.id]: `${formatQuantityValue(rate, false)} | $${formatRateValue(rate?.rate, rate?.unit)}`,
      },
    };
  });

  return [
    createHeaderRow("Age Banded Rates", plan.id),
    createColumnHeaderRow("Age Band", plan.id, "Vol | Rate"),
    ...dataRows,
  ];
}

/**
 * Build Excel rows for all plans in the benefit plan groups
 *
 * Builds one shared row set for the worksheet by aligning rows with a stable
 * section-and-label key. Plans with the same rate design share rows; plans with
 * different designs keep separate sections to avoid misleading mixed rows.
 */
export function buildRateRowsForAllPlans(
  benefitPlanGroups: BenefitPlanGroup[],
  rateSchedulesByPlanId: Map<string, RateSchedule[]>,
  includeDependentRateBasisInLabel = false,
): ExcelRow[] {
  const orderedRowKeys: string[] = [];
  const rowsByKey = new Map<string, ExcelRow>();

  benefitPlanGroups.forEach((group) => {
    group.benefitPlans.forEach((plan) => {
      const sections = buildRateSectionsForPlan(
        plan,
        rateSchedulesByPlanId.get(plan.id) || [],
        includeDependentRateBasisInLabel,
      );

      sections.forEach((section) => {
        mergeSectionRows(plan, section, orderedRowKeys, rowsByKey);
      });
    });
  });

  return orderedRowKeys
    .map((rowKey) => rowsByKey.get(rowKey))
    .filter((row): row is ExcelRow => row !== undefined)
    .filter((row) => row.label !== "" || Object.keys(row.values).length > 0);
}
